import React from 'react';
import { Icon, Label, Popup } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

/**
 * Discreet warning shown next to a reader application when the account's
 * footprint does not add up — a very new account that has taken on a lot of
 * manuscripts and finished none.
 *
 * This is INFORMATION, not enforcement. Nothing is blocked, hidden or
 * auto-declined; the author still approves or declines as usual. The copy stays
 * observational (what the account did) rather than accusatory ("this is a bot"),
 * because a false positive here is a warning shown about a real person.
 *
 * The server decides whether to flag (routes/readers/join-requests/
 * readerActivitySignal.js) so the thresholds live in one env-tunable place; the
 * client only renders what it is given.
 */
export const UnusualActivityBadge = ({ unusualActivity, t }) => {
  if (!unusualActivity) return null;

  const { books = 0, accountAgeDays = 0 } = unusualActivity;
  // "in 0 days" reads badly for a same-day account, and the same-day case is the
  // most striking one, so it gets its own phrasing.
  const tooltip = accountAgeDays === 0
    ? t('UnusualActivityTooltipToday', { books })
    : t('UnusualActivityTooltip', { books, count: accountAgeDays });

  return (
    <Popup
      // hover alone would leave the tooltip unreachable on touch devices, where
      // a good share of authors triage their applications
      on={['hover', 'click']}
      position="top left"
      content={tooltip}
      trigger={(
        <Label
          basic
          color="orange"
          size="tiny"
          style={{ cursor: 'help', marginLeft: 0, whiteSpace: 'nowrap' }}
          data-testid="unusual-activity-badge"
          aria-label={`${t('UnusualActivity')}. ${tooltip}`}
        >
          <Icon name="exclamation triangle" />
          {t('UnusualActivity')}
        </Label>
      )}
    />
  );
};

export default withTranslation()(UnusualActivityBadge);
